/**
 * React Query hooks for Recruitment Type operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import recruitmentTypeService from '../services/recruitment-type.service';

interface UseRecruitmentTypeListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useRecruitmentTypeListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'name',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UseRecruitmentTypeListQueryParams) => {
  return useQuery({
    queryKey: ['recruitment-types', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await recruitmentTypeService.listRecruitmentTypes(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseRecruitmentTypeDetailQueryParams {
  recruitmentTypeId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useRecruitmentTypeDetailQuery = ({
  recruitmentTypeId,
  workspaceId,
  idToken,
  enabled = true,
}: UseRecruitmentTypeDetailQueryParams) => {
  return useQuery({
    queryKey: ['recruitment-type', workspaceId, recruitmentTypeId],
    queryFn: async () => {
      if (!recruitmentTypeId || !workspaceId || !idToken) {
        throw new Error('Recruitment type ID, workspace ID, and token are required');
      }

      return await recruitmentTypeService.getRecruitmentType(workspaceId, recruitmentTypeId, idToken);
    },
    enabled: enabled && !!workspaceId && !!recruitmentTypeId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
